/**
 * Compact number formatting — 4,900 → "4.9K", 21,400,000 → "21.4M".
 *
 * ONE implementation, because there were ten.
 *
 * `formatFollowers` (DropHero), `compactCount` (PublisherHero), `compact`
 * (OtherGamesSection, rosterMember), `formatStat` (Hero, DropInSubRail),
 * `formatViews` (shared), `formatViewerCount` (StreamTheater,
 * StreamingSection) and `formatPlayerCount` (gameHelpers) were the same four
 * lines written ten times — and they had already drifted apart:
 *
 *   - Seven render thousands with one decimal ("4.9K"); three round to the
 *     nearest thousand ("5K"). The same creator's follower count therefore read
 *     differently on the creator hero and the publisher hero of the same Drop.
 *   - Zero rendered as "0", "", "—" or "0" depending on which copy ran.
 *
 * One decimal wins: it is what the majority already did and what the design
 * anchors show ("21.4M"). The zero case stays per-caller via `empty`, because
 * a dash reads correctly in a stat row and terribly in a chip.
 */

export interface FormatCompactNumberOptions {
    /**
     * What to render for a missing, non-numeric or non-positive value.
     *
     * Defaults to "0" — the literal truth — rather than an empty string, so a
     * caller that forgets to think about it gets something visible instead of a
     * silently collapsed layout.
     */
    empty?: string;
}

export function formatCompactNumber(n?: number, options: FormatCompactNumberOptions = {}): string {
    const {empty = "0"} = options;

    if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return empty;

    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(n);
}
